import React from "react";
import type { FieldValues, Path } from "react-hook-form";
import { useController } from "react-hook-form";

import { FieldLabel, FlexBox, Input, StatusText } from "@braze/beacon-core";

import useI18nNamespace from "@services/useI18nNamespace";

import { CountryCodeSelect } from "./CountryCodeSelect";
import type { PhoneNumberInputFieldProps } from "./types";

export const PhoneNumberInputField = <FormValues extends FieldValues = FieldValues>({
  label,
  phoneNumberCountryFieldName,
  phoneNumberFieldName,
  control,
  onKeyDown,
  disabled,
}: PhoneNumberInputFieldProps<FormValues>): React.ReactElement => {
  const { t } = useI18nNamespace("free-trial-registration");

  const {
    field: phoneNumberField,
    fieldState: { error: phoneNumberError, invalid: phoneNumberInvalid },
  } = useController({
    name: phoneNumberFieldName as Path<FormValues>,
    control,
  });

  const {
    field: phoneNumberCountryField,
    fieldState: { error: phoneNumberCountryError, invalid: phoneNumberCountryInvalid },
  } = useController({
    name: phoneNumberCountryFieldName as Path<FormValues>,
    control,
  });

  return (
    <FieldLabel
      label={label}
      htmlFor="phone-number-field"
      status="danger"
      statusText={phoneNumberCountryError?.message || phoneNumberError?.message}
      data-cy="phoneNumberField"
    >
      <FlexBox dir="row" justifyContent="flex-start">
        <CountryCodeSelect
          data-cy="countryCodeSelect"
          {...phoneNumberCountryField}
          invalid={phoneNumberCountryInvalid}
          isDisabled={disabled}
          onChange={(e) => {
            phoneNumberCountryField.onChange(e);
            phoneNumberCountryField.onBlur();
          }}
        />
        <Input
          type="text"
          pattern="[0-9]*"
          id={phoneNumberFieldName.toString()}
          placeholder={t("phone-number-placeholder", "Enter your mobile phone number")}
          fullWidth
          {...phoneNumberField}
          invalid={phoneNumberInvalid}
          disabled={disabled}
          onKeyDown={onKeyDown}
        />
      </FlexBox>
      <StatusText status="info">
        {t("rates-warning", "This number will be used for 2FA. Message and data rates may apply.")}
      </StatusText>
    </FieldLabel>
  );
};
